import { Spin } from 'antd';
import { CaseSidebarItemId } from '@components/CaseSidebar/types';
import { FormValidationProvider } from '@hooks/useBtlFormValidation';
import { isOoProductEditableStatus } from '@components/CaseSidebar/config/ooStatus';
import { useBtlApplicationInProgressById } from '@queries/btlApplications/useApplicationInProgressById/useBtlApplicationInProgressById';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams, useSearchParams } from 'react-router-dom';
import { OoCaseFormFooter } from './OoCaseFormFooter';
import { isOoApplicantIndividualFormEditable } from './Applicants/CaseApplicantIndividualPage';
import { OO_CREATE_INDIVIDUAL_SEARCH_PARAM } from './Applicants/CaseApplicantsList';
import { isOoFinancingFormEditable } from '@pages/OoApplicationViewPage/Financing/useOoCaseFinancingForm';
import { isOoExpensesFormEditable } from './Expenses/CaseExpensesPage';
import { isOoPropertyFormEditable } from '@pages/OoApplicationViewPage/Property/useOoCasePropertyForm';
import { isOoSolicitorsFormEditable } from '@pages/OoApplicationViewPage/Solicitors/useOoCaseSolicitorsForm';
import { OoCaseSidebar } from './OoCaseSidebar';
import { useOoApplicationViewData } from './useOoApplicationViewData';
import { OoFormSectionContent } from './OoFormSectionContent';
import { useOoFormMappedRoutes } from './useOoFormMappedRoutes';
import { useOoSectionNavigation } from './useOoSectionNavigation';

interface OoApplicationViewPageContentProps {
  caseId: string;
}

function OoApplicationViewPageContent({ caseId }: OoApplicationViewPageContentProps) {
  const viewData = useOoApplicationViewData(caseId);
  const { formSectionParam, updateSectionInUrl } = useOoSectionNavigation(viewData.sidebarContext);
  const [searchParams] = useSearchParams();
  const [openBankDetailsInEditMode, setOpenBankDetailsInEditMode] = useState(false);

  const handleGoToEvidence = useCallback(() => {
    updateSectionInUrl(CaseSidebarItemId.EVIDENCE);
  }, [updateSectionInUrl]);

  const handleGoToBankDetails = useCallback(() => {
    setOpenBankDetailsInEditMode(true);
    updateSectionInUrl(CaseSidebarItemId.BANK_DETAILS);
  }, [updateSectionInUrl]);

  const handleOpenBankDetailsInEditModeConsumed = useCallback(() => {
    setOpenBankDetailsInEditMode(false);
  }, []);

  const routeContext = useMemo(
    () => ({
      ...viewData,
      openBankDetailsInEditMode,
      onOpenBankDetailsInEditModeConsumed: handleOpenBankDetailsInEditModeConsumed,
      onGoToEvidence: handleGoToEvidence,
      onGoToBankDetails: handleGoToBankDetails,
    }),
    [
      viewData,
      openBankDetailsInEditMode,
      handleOpenBankDetailsInEditModeConsumed,
      handleGoToEvidence,
      handleGoToBankDetails,
    ],
  );

  const { sectionTitleKey, currentSection, previousSection, nextSection, previousLabel, nextLabel } =
    useOoFormMappedRoutes(routeContext);

  const isPropertyFormEditable =
    currentSection === CaseSidebarItemId.PROPERTY && isOoPropertyFormEditable(viewData.case);

  const isSolicitorsFormEditable =
    currentSection === CaseSidebarItemId.SOLICITORS && isOoSolicitorsFormEditable(viewData.case);

  const isFinancingFormEditable =
    currentSection === CaseSidebarItemId.FINANCING &&
    isOoFinancingFormEditable(viewData.case, viewData.sidebarContext.canAccessHppPages);

  const isApplicantIndividualFormEditable =
    currentSection === CaseSidebarItemId.APPLICANTS &&
    (Boolean(searchParams.get('individualId')) || searchParams.get(OO_CREATE_INDIVIDUAL_SEARCH_PARAM) === '1') &&
    isOoApplicantIndividualFormEditable(viewData.case);

  const isExpensesFormEditable =
    currentSection === CaseSidebarItemId.EXPENSES && isOoExpensesFormEditable(viewData.case);

  const isProductSection = currentSection === CaseSidebarItemId.PRODUCT && viewData.sidebarContext.canAccessHppPages;
  const isAffordabilitySection =
    currentSection === CaseSidebarItemId.AFFORDABILITY_CHECK && viewData.sidebarContext.canAccessHppPages;
  const isSubmitSection =
    currentSection === CaseSidebarItemId.SUBMIT_DIP || currentSection === CaseSidebarItemId.SUBMIT_APPLICATION;
  const isProductEditable = isOoProductEditableStatus(viewData.case?.status);
  const isHppFormSection = isProductSection || isAffordabilitySection;
  const isFormValidationSection = isHppFormSection || isSubmitSection;
  const isHppFormSectionEditable = (isProductSection && isProductEditable) || isAffordabilitySection;

  useEffect(() => {
    if (currentSection !== CaseSidebarItemId.BANK_DETAILS) {
      setOpenBankDetailsInEditMode(false);
    }
  }, [currentSection]);

  const formContent = (
    <>
      <h2 className='mt-0 mb-1 text-2xl font-bold leading-tight text-night-sky'>
        <FormattedMessage id={sectionTitleKey} />
      </h2>
      <div id='form-scroll-container' className='custom-scrollbar mb-16 grow overflow-auto pb-4 pl-2 pr-4 pt-2'>
        <OoFormSectionContent currentSection={currentSection} context={routeContext} />
      </div>
      {!isPropertyFormEditable &&
        !isSolicitorsFormEditable &&
        !isFinancingFormEditable &&
        !isApplicantIndividualFormEditable &&
        !isExpensesFormEditable &&
        !isSubmitSection && (
          <OoCaseFormFooter
            previousSection={previousSection}
            nextSection={nextSection}
            previousLabel={previousLabel}
            nextLabel={nextLabel}
            onNavigate={updateSectionInUrl}
            isFormEditable={isHppFormSectionEditable}
          />
        )}
    </>
  );

  return (
    <div className='-mt-16 flex h-full min-h-0 bg-gray-100'>
      <div className='flex h-full min-h-0 shrink-0'>
        <OoCaseSidebar
          caseData={viewData.case}
          isCaseLoading={viewData.isCaseLoading}
          currentSection={formSectionParam ?? undefined}
          context={viewData.sidebarContext}
        />
      </div>
      <div className='flex min-h-0 flex-1 gap-8 p-6 pb-4 pl-8'>
        <div className='relative flex h-full grow flex-col pt-4'>
          {isFormValidationSection ? <FormValidationProvider>{formContent}</FormValidationProvider> : formContent}
        </div>
      </div>
    </div>
  );
}

export const OoApplicationViewPage = () => {
  const params = useParams();
  const caseId = params?.id || '';
  const { data: caseData, isLoading } = useBtlApplicationInProgressById(caseId);

  if (isLoading || !caseData) {
    return (
      <div className='-mt-16 flex h-full min-h-0 items-center justify-center bg-gray-100'>
        <Spin size='large' />
      </div>
    );
  }

  return <OoApplicationViewPageContent caseId={caseId} />;
};
